import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import useWorkflowStore, { WORKFLOW_ACTIONS } from '../../stores/workflowStore';

/**
 * SmartSuggestionBar — Intuitive inline next-step guide for every visit card.
 *
 * Design philosophy:
 *   Works IMMEDIATELY for all roles (Doctor, Nurse, Admin, Receptionist)
 *   with zero onboarding or seed data. Smart defaults are generated
 *   client-side based on visit.status + userRole + completed actions.
 *   Backend Markov predictions enhance/override when available.
 *
 * How it works:
 *   1. Instantly renders role+status-aware suggestions (no API needed)
 *   2. Background-fetches AI-predicted suggestions to enhance defaults
 *   3. Tracks completed actions this session to avoid re-suggesting
 *   4. Shows step-completion checkmarks for user feedback
 *
 * Renders as a subtle action strip below the visit status buttons.
 */


// ── Local suggestion generation (zero-latency, no API) ──────────────
//
// DESIGN RULES:
//   1. Never duplicate buttons already visible on the card
//      (Start, Complete, Cancel, Details, Upload Prescription, History)
//   2. Only suggest DATA-ENTRY actions the user hasn't done yet
//   3. Upload Report → Nurse / Receptionist only
//   4. View History → Doctor / Admin only (already has a button, so omitted)
//   5. After completing an action, surface the next logical steps
//      but don't lock into a rigid path — user can dismiss or pick another
//
// The bar answers: "What data should I capture for this visit right now?"

/**
 * All possible data-entry suggestions per role.
 * These are the ONLY actions shown — status transitions (Start/Complete/Cancel)
 * and navigation (Details/History) are already buttons on the card.
 */
const ROLE_SUGGESTIONS = {
  DOCTOR: [
    { action: 'ADD_VITALS',       label: 'Vitals',       icon: '🌡️', reason: 'Record patient vitals' },
    { action: 'ADD_SYMPTOMS',     label: 'Symptoms',     icon: '🤒', reason: 'Note presenting complaints' },
    { action: 'ADD_NOTES',        label: 'Notes',        icon: '📝', reason: 'Add clinical notes' },
    { action: 'ADD_PRESCRIPTION', label: 'Prescribe',    icon: '💊', reason: 'Write prescription', primary: true },
    { action: 'GENERATE_PRESCRIPTION', label: 'Print Rx', icon: '🖨️', reason: 'Generate printable prescription' },
    { action: 'OPEN_VISIT',       label: 'View Reports', icon: '📄', reason: 'Check attached lab/imaging reports' },
  ],
  NURSE: [
    { action: 'ADD_VITALS',   label: 'Vitals',        icon: '🌡️', reason: 'Record vitals before consultation', primary: true },
    { action: 'ADD_SYMPTOMS', label: 'Symptoms',      icon: '🤒', reason: 'Note presenting complaints' },
    { action: 'ADD_NOTES',    label: 'Notes',          icon: '📝', reason: 'Add nursing notes' },
    { action: 'ADD_REPORT',   label: 'Upload Report',  icon: '📄', reason: 'Attach lab/imaging report' },
  ],
  ADMIN: [
    { action: 'ADD_VITALS',   label: 'Vitals',   icon: '🌡️', reason: 'Record patient vitals' },
    { action: 'ADD_NOTES',    label: 'Notes',     icon: '📝', reason: 'Add notes' },
    { action: 'ADD_SYMPTOMS', label: 'Symptoms',  icon: '🤒', reason: 'Record symptoms' },
  ],
  RECEPTIONIST: [
    { action: 'ADD_REPORT', label: 'Upload Report', icon: '📄', reason: 'Attach patient documents', primary: true },
    { action: 'ADD_NOTES',  label: 'Notes',          icon: '📝', reason: 'Add a note to this visit' },
  ],
  _DEFAULT: [
    { action: 'ADD_NOTES', label: 'Notes', icon: '📝', reason: 'Add notes to this visit' },
  ],
};

/**
 * Build local suggestions:
 *   - Only for QUEUE and IN_PROGRESS (terminal states don't need suggestions)
 *   - Filter out actions already completed this session
 *   - Re-order: if user just did X, promote the natural next step
 */
function getLocalSuggestions(visitStatus, role, completedActions) {
  if (visitStatus !== 'QUEUE' && visitStatus !== 'IN_PROGRESS') return [];

  const roleUpper = (role || '').toUpperCase();
  const pool = ROLE_SUGGESTIONS[roleUpper] || ROLE_SUGGESTIONS._DEFAULT;
  const done = new Set(completedActions || []);

  // Filter out already-done actions
  let available = pool.filter((s) => !done.has(s.action));

  // If everything is done, nothing to suggest (the bar hides gracefully)
  if (!available.length) return [];

  // Re-order based on last completed action (journey awareness)
  const last = completedActions?.[completedActions.length - 1];
  if (last) {
    available = reorderAfterAction(available, last, roleUpper);
  }

  return available.map((s, idx) => ({
    ...s,
    confidence: s.primary ? 0.85 : Math.max(0.3, 0.75 - idx * 0.15),
    auto_progress: false,
  }));
}

/**
 * After completing an action, promote the natural next step.
 * This creates "journey awareness" without locking the user in.
 */
function reorderAfterAction(suggestions, lastAction, role) {
  // Natural next-step map: "after X, Y is most likely next"
  const NEXT_STEP = {
    ADD_VITALS: 'ADD_SYMPTOMS',        // Vitals → Symptoms
    ADD_SYMPTOMS: 'ADD_NOTES',         // Symptoms → Notes
    ADD_NOTES: 'ADD_PRESCRIPTION',     // Notes → Prescription (for doctors)
    ADD_PRESCRIPTION: 'GENERATE_PRESCRIPTION',  // Prescription → Print Rx
    GENERATE_PRESCRIPTION: null,                 // Print Rx → done
    ADD_REPORT: 'ADD_NOTES',           // Report → Notes
  };

  const promoted = NEXT_STEP[lastAction];
  if (!promoted) return suggestions;

  const idx = suggestions.findIndex((s) => s.action === promoted);
  if (idx <= 0) return suggestions; // already first or not found

  // Move the promoted item to the front and mark it primary
  const copy = [...suggestions];
  const [item] = copy.splice(idx, 1);
  copy.unshift({ ...item, primary: true });
  // Un-primary the rest
  return copy.map((s, i) => (i === 0 ? s : { ...s, primary: false }));
}


// ── Main Component ──────────────────────────────────────────────────

export default function SmartSuggestionBar({
  visit,
  userRole,
  onStartVisit,
  onCompleteVisit,
  onCancelVisit,
  onOpenDetails,
  onOpenHistory,
  onOpenVitals,
  onOpenNotes,
  onOpenSymptoms,
  onOpenPrescription,
  onOpenReport,
  onGeneratePrescription,
  onStartNextVisit,
  lastCompletedAction,
}) {
  const { isDark } = useTheme();

  const {
    visitSuggestions,
    fetchSuggestions,
    dismissSuggestion,
  } = useWorkflowStore();

  const visitId = visit?.visit_id;
  const visitStatus = visit?.status;

  // ── Track completed actions this session per visit ────────────────
  const [completedActions, setCompletedActions] = useState([]);

  useEffect(() => {
    if (lastCompletedAction && !completedActions.includes(lastCompletedAction)) {
      setCompletedActions((prev) => [...prev, lastCompletedAction]);
    }
  }, [lastCompletedAction]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Local suggestions (instant, no API) ───────────────────────────
  const localSuggestions = useMemo(
    () => getLocalSuggestions(visitStatus, userRole, completedActions),
    [visitStatus, userRole, completedActions]
  );

  // ── Background: fetch AI-predicted suggestions (enhancement layer)
  useEffect(() => {
    if (!visitId) return;
    fetchSuggestions(visitId, lastCompletedAction, visitStatus);
  }, [visitId, lastCompletedAction, visitStatus, fetchSuggestions]);

  // ── Merge: AI predictions override local defaults when available ──
  const cached = visitSuggestions[visitId];
  const aiSuggestions = useMemo(() => cached?.suggestions || [], [cached?.suggestions]);

  const suggestions = useMemo(() => {
    // If AI returned results, use them (they're already role/context filtered)
    if (aiSuggestions.length > 0) {
      // Filter out completed actions from AI suggestions too
      const done = new Set(completedActions);
      return aiSuggestions.filter((s) => !done.has(s.action));
    }
    // Otherwise use the instant local defaults
    return localSuggestions;
  }, [aiSuggestions, localSuggestions, completedActions]);

  // ── Map action to handler ─────────────────────────────────────────
  const handleSuggestionClick = useCallback(
    (suggestion) => {
      const action = suggestion.action;
      switch (action) {
        case WORKFLOW_ACTIONS.MARK_IN_PROGRESS:
          onStartVisit?.(visitId);
          break;
        case WORKFLOW_ACTIONS.MARK_COMPLETED:
          onCompleteVisit?.(visitId);
          break;
        case WORKFLOW_ACTIONS.MARK_CANCELLED:
          onCancelVisit?.(visitId);
          break;
        case WORKFLOW_ACTIONS.ADD_VITALS:
          onOpenVitals?.(visitId);
          break;
        case WORKFLOW_ACTIONS.ADD_NOTES:
          onOpenNotes?.(visitId);
          break;
        case WORKFLOW_ACTIONS.ADD_SYMPTOMS:
          onOpenSymptoms?.(visitId);
          break;
        case WORKFLOW_ACTIONS.ADD_PRESCRIPTION:
          onOpenPrescription?.(visitId);
          break;
        case WORKFLOW_ACTIONS.ADD_REPORT:
          onOpenReport?.(visitId);
          break;
        case WORKFLOW_ACTIONS.GENERATE_PRESCRIPTION:
          onGeneratePrescription?.(visitId);
          break;
        case WORKFLOW_ACTIONS.VIEW_PATIENT_HISTORY:
          onOpenHistory?.();
          break;
        case WORKFLOW_ACTIONS.OPEN_VISIT:
          onOpenDetails?.(visitId);
          break;
        case WORKFLOW_ACTIONS.START_NEXT_VISIT:
          onStartNextVisit?.();
          break;
        default:
          break;
      }
    },
    [
      visitId,
      onStartVisit,
      onCompleteVisit,
      onCancelVisit,
      onOpenDetails,
      onOpenHistory,
      onOpenVitals,
      onOpenNotes,
      onOpenSymptoms,
      onOpenPrescription,
      onOpenReport,
      onGeneratePrescription,
      onStartNextVisit,
    ]
  );

  const handleDismiss = useCallback(
    (action) => {
      dismissSuggestion(visitId, action);
      // Also add to completed so it doesn't reappear from local defaults
      setCompletedActions((prev) => [...prev, action]);
    },
    [visitId, dismissSuggestion]
  );

  // ── Don't render for terminal statuses ────────────────────────────
  if (visitStatus === 'COMPLETED' || visitStatus === 'CANCELLED') {
    return null;
  }

  // Don't render if truly nothing to suggest
  if (!suggestions.length) return null;

  // ── Styles ────────────────────────────────────────────────────────
  const barStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    marginTop: '0.5rem',
    borderRadius: '8px',
    background: isDark
      ? 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(13, 148, 136,0.06))'
      : 'linear-gradient(135deg, rgba(59,130,246,0.05), rgba(13, 148, 136,0.03))',
    border: `1px solid ${isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)'}`,
    fontSize: '0.8rem',
    flexWrap: 'wrap',
    transition: 'background 0.2s ease, border-color 0.2s ease',
    animation: 'suggestSlideIn 0.3s ease-out',
  };

  const labelStyle = {
    color: isDark ? '#94a3b8' : '#64748b',
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  };

  const completedLabel = completedActions.length > 0 ? (
    <span style={{
      color: isDark ? '#4ade80' : '#16a34a',
      fontSize: '0.65rem',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '0.15rem',
      marginRight: '0.25rem',
    }}>
      ✔ {completedActions.length}
    </span>
  ) : null;

  return (
    <div style={barStyle}>
      {completedLabel}

      {/* Label */}
      <span style={labelStyle}>
        <span style={{ fontSize: '0.7rem' }}>⚡</span>
        Next
      </span>

      {/* Suggestion pills */}
      {suggestions.map((suggestion) => (
        <SuggestionPill
          key={suggestion.action}
          suggestion={suggestion}
          isDark={isDark}
          onClick={() => handleSuggestionClick(suggestion)}
          onDismiss={() => handleDismiss(suggestion.action)}
          isAutoProgress={suggestion.auto_progress}
          isPrimary={suggestion.primary}
        />
      ))}

      {/* Inline CSS animation */}
      <style>{`
        @keyframes suggestSlideIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes autoPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.3); }
          50% { box-shadow: 0 0 0 4px rgba(59,130,246,0.1); }
        }
      `}</style>
    </div>
  );
}


// ── SuggestionPill — individual suggestion button ───────────────────

function SuggestionPill({ suggestion, isDark, onClick, onDismiss, isAutoProgress, isPrimary }) {
  const [hovered, setHovered] = useState(false);

  const confidence = suggestion.confidence || 0;
  const isHigh = isPrimary || confidence >= 0.7;
  const isMedium = !isHigh && confidence >= 0.4;

  const pillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    padding: '0.3rem 0.6rem',
    borderRadius: '6px',
    fontSize: '0.78rem',
    fontWeight: isPrimary ? 600 : 500,
    cursor: 'pointer',
    transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
    border: '1px solid transparent',
    position: 'relative',
    ...(isHigh
      ? {
          background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)',
          color: isDark ? '#60a5fa' : '#2563eb',
          borderColor: isDark ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.2)',
        }
      : isMedium
      ? {
          background: isDark ? 'rgba(13, 148, 136,0.1)' : 'rgba(13, 148, 136,0.06)',
          color: isDark ? '#2dd4bf' : '#0f766e',
          borderColor: isDark ? 'rgba(13, 148, 136,0.2)' : 'rgba(13, 148, 136,0.15)',
        }
      : {
          background: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(148,163,184,0.06)',
          color: isDark ? '#94a3b8' : '#64748b',
          borderColor: isDark ? 'rgba(148,163,184,0.15)' : 'rgba(148,163,184,0.1)',
        }),
    ...(hovered
      ? {
          transform: 'translateY(-1px)',
          boxShadow: isDark
            ? '0 2px 8px rgba(0,0,0,0.3)'
            : '0 2px 8px rgba(0,0,0,0.1)',
        }
      : {}),
    ...(isAutoProgress
      ? { animation: 'autoPulse 2s ease-in-out infinite' }
      : {}),
  };

  return (
    <button
      style={pillStyle}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={suggestion.reason}
      aria-label={`${suggestion.label} — ${suggestion.reason}`}
    >
      {suggestion.icon && (
        <span style={{ fontSize: '0.85rem' }}>{suggestion.icon}</span>
      )}
      <span>{suggestion.label}</span>

      {isAutoProgress && (
        <span style={{
          fontSize: '0.6rem',
          background: isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.1)',
          padding: '0.1rem 0.3rem',
          borderRadius: '3px',
          fontWeight: 600,
        }}>
          AUTO
        </span>
      )}

      {/* Dismiss on hover */}
      <span
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        style={{
          fontSize: '0.6rem',
          opacity: hovered ? 0.7 : 0,
          cursor: 'pointer',
          marginLeft: '0.1rem',
          transition: 'opacity 0.15s',
        }}
        title="Dismiss"
      >
        ✕
      </span>
    </button>
  );
}
